import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Save, Plus, Trash2, Edit, X, Eye, EyeOff, Loader2, Image as ImageIcon,
  Star, Mail, User, Phone, Lock, Shield, FileText, ChevronDown, ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SmtpSelfTestPanel = ({ adminPassword }) => {
  const [to, setTo] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const run = async () => {
    if (!to || !to.includes('@')) {
      setResult({ ok: false, message: 'Enter a valid recipient email first.' });
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/smtp-test?to_email=${encodeURIComponent(to)}`, {
        method: 'POST',
        headers: { 'X-Admin-Password': adminPassword || '' },
      });
      const data = await r.json();
      if (!r.ok) {
        setResult({ ok: false, message: data.detail || `HTTP ${r.status}`, config: null });
      } else {
        setResult(data);
      }
    } catch (e) {
      setResult({ ok: false, message: `Network error: ${e.message}` });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t pt-6" data-testid="smtp-test-panel">
      <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Self-Test (E-Signature email diagnostic)</label>
      <p className="text-sm text-gray-500 mb-2">
        Send a one-line test email through the same Hostinger SMTP pipeline that powers e-signature requests.
        Use this on production to confirm the deploy environment has the correct credentials AND the
        recipient mailserver is accepting our messages.
      </p>
      <div className="flex gap-3 flex-wrap">
        <input
          type="email"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="recipient@example.com"
          className="flex-1 min-w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6]"
          data-testid="smtp-test-to"
        />
        <Button onClick={run} disabled={busy} className="bg-[#003D5C]" data-testid="smtp-test-send-btn">
          {busy ? 'Sending…' : 'Send Test Email'}
        </Button>
      </div>
      {result && (
        <div
          className={`mt-3 p-3 rounded-lg border text-sm ${
            result.ok ? 'bg-green-50 border-green-300 text-green-900' : 'bg-red-50 border-red-300 text-red-900'
          }`}
          data-testid="smtp-test-result"
        >
          <div className="font-semibold mb-1">{result.ok ? '✓' : '⚠'} {result.message}</div>
          {result.server_response && (
            <div className="text-xs"><b>SMTP response:</b> <code>{result.server_response}</code></div>
          )}
          {result.config && (
            <ul className="text-xs mt-2 space-y-0.5">
              <li><b>Host:</b> <code>{String(result.config.smtp_host)}</code></li>
              <li><b>Port:</b> <code>{String(result.config.smtp_port)}</code></li>
              <li><b>User:</b> <code>{String(result.config.smtp_user)}</code></li>
              <li><b>Password configured:</b> <code>{String(result.config.smtp_password_configured)}</code></li>
              <li><b>Sender:</b> <code>{String(result.config.sender_email)}</code></li>
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
